import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  FormGroup,
  Switch,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  getLiveState,
  setLiveEnabled,
} from '../../app/service/bridge/bridgeKillswitchApi';
import { useLocalizedMessage } from '../../util/useLocalizedMessage';
import {
  enqueueErrorSnackbar,
  enqueueSuccessSnackbar,
} from '../../actions/Notifications';

const REASON_NONE = 'none';
const REASON_KILL_SWITCH = 'kill_switch';
const REASON_MODULE_DISABLED = 'module_disabled';

// Source values emitted via onLiveStateChange. 'pending' is the only new
// one — it is fired when deferWrite is true and the user has toggled the
// switch, but the bridge write has not happened yet. The parent is
// expected to commit by calling setLiveEnabled() itself.
const SOURCE_PENDING = 'pending';

function statusKeyFor(reason) {
  if (reason === REASON_MODULE_DISABLED) {
    return 'search.settings.live.status.module_disabled';
  }
  if (reason === REASON_KILL_SWITCH) {
    return 'search.settings.live.status.hidden';
  }
  return 'search.settings.live.status.live';
}

/**
 * LiveKillSwitchControl — reusable bridge-only kill-switch.
 *
 * Owns its own state, its own bridge reads/writes, and (when `showStatus`
 * is true) its own status display. Consumers:
 *
 *   - OperatorHome (production) embeds it with `showStatus={false}` so
 *     the surrounding live <StatusCard> provides the merchant-facing
 *     status text and the control renders only the switch + saving +
 *     error UI.
 *
 *   - The dedicated /search/settings/live dev route (LivePage.jsx) uses
 *     `showStatus` (default true) so the page is self-contained: status
 *     block + switch + refresh + error.
 *
 * Sync contract (onLiveStateChange):
 *   When provided, the control invokes `onLiveStateChange(payload)` after
 *   every state transition. Payload shape:
 *     { enabled: boolean, reason: 'none' | 'kill_switch' | 'module_disabled',
 *       source: 'load' | 'pending' | 'optimistic' | 'success' | 'rollback' | 'load_error' }
 *   Callers can use this to keep an external status display (e.g. an
 *   OperatorHome StatusCard) in sync without a full refetch. The
 *   'pending' source is fired only when deferWrite=true and the user
 *   has flipped the switch — the parent page is expected to commit the
 *   change via its own Save button.
 *
 * Contract:
 *   - Read state via getLiveState() (onboarding.state, fallback app.bootstrap).
 *   - Show "Live" / "Hidden from shoppers" / "Module disabled" copy
 *     depending on enabled + reason.
 *   - Main toggle is enabled when capability exists AND module is not disabled.
 *   - Toggle off: optimistic UI. If deferWrite=false, call
 *     setLiveEnabled(false) and rollback on error. If deferWrite=true,
 *     skip the bridge call and only emit
 *     onLiveStateChange({ ..., source: 'pending' }).
 *   - Toggle on: same shape — deferWrite=true skips the bridge call.
 *     If reason === "module_disabled", do NOT call write at all and show
 *     module-disabled copy.
 *   - Disabled + tooltip when capability is missing or module is disabled.
 */
const LiveKillSwitchControl = ({
  onRegisterRef,
  onLiveStateChange,
  showStatus = true,
  compact = false,
  hideRefresh = false,
  deferWrite = false,
  bare = false,
}) => {
  const localizeMessage = useLocalizedMessage();
  const dispatch = useDispatch();
  const [live, setLive] = useState(null);
  const [capability, setCapability] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const next = await getLiveState();
      const nextLive = { enabled: next.enabled === true, reason: next.reason };
      const nextCap = next.capability === true;
      setLive(nextLive);
      setCapability(nextCap);
      if (onLiveStateChange) {
        onLiveStateChange({ ...nextLive, source: 'load' });
      }
    } catch (e) {
      setLive({ enabled: false, reason: REASON_KILL_SWITCH });
      setCapability(false);
      setErrorMessage(e && e.message ? e.message : 'load_failed');
      if (onLiveStateChange) {
        onLiveStateChange({
          enabled: false,
          reason: REASON_KILL_SWITCH,
          source: 'load_error',
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [onLiveStateChange]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (onRegisterRef) {
      onRegisterRef({ reload: load });
    }
  }, [onRegisterRef, load]);

  const handleToggle = useCallback(
    async (nextValue) => {
      // Spec: if module is disabled, do NOT call write. Status copy is
      // already shown by the status block.
      if (live && live.reason === REASON_MODULE_DISABLED) return;
      if (!capability) return;

      const previous = live;
      // Optimistic UI: flip locally first, then call the bridge.
      const optimistic = {
        enabled: nextValue,
        reason: nextValue ? REASON_NONE : REASON_KILL_SWITCH,
      };
      setLive(optimistic);
      if (onLiveStateChange) {
        onLiveStateChange({
          ...optimistic,
          source: deferWrite ? SOURCE_PENDING : 'optimistic',
        });
      }

      if (deferWrite) {
        // Parent owns the actual bridge write. We just track the
        // optimistic state and report it as 'pending'. No bridge call,
        // no toast — the parent's Save button drives the commit.
        return;
      }

      setIsSaving(true);
      setErrorMessage(null);
      try {
        const result = await setLiveEnabled(nextValue);
        const confirmed = { enabled: result.enabled === true, reason: result.reason };
        setLive(confirmed);
        if (onLiveStateChange) {
          onLiveStateChange({ ...confirmed, source: 'success' });
        }
        dispatch(
          enqueueSuccessSnackbar(
            localizeMessage(
              result.enabled
                ? 'search.settings.live.toast.enabled'
                : 'search.settings.live.toast.disabled',
            ),
          ),
        );
      } catch (e) {
        // Rollback to previous state.
        if (previous) {
          setLive(previous);
          if (onLiveStateChange) {
            onLiveStateChange({ ...previous, source: 'rollback' });
          }
        }
        const msg = (e && e.message) || 'save_failed';
        setErrorMessage(msg);
        dispatch(
          enqueueErrorSnackbar(
            localizeMessage('search.settings.live.toast.error', {
              error: msg,
            }),
          ),
        );
      } finally {
        setIsSaving(false);
      }
    },
    [live, capability, deferWrite, dispatch, localizeMessage, onLiveStateChange],
  );

  if (isLoading) {
    if (compact) {
      return (
        <Box
          data-testid="live-settings-loading"
          data-compact="true"
          display="flex"
          alignItems="center"
        >
          <CircularProgress size={16} />
        </Box>
      );
    }
    return (
      <Box
        data-testid="live-settings-loading"
        p={2}
        display="flex"
        alignItems="center"
      >
        <CircularProgress size={20} />
        <Box ml={2}>
          <Typography variant="body2">
            {localizeMessage('search.settings.live.loading')}
          </Typography>
        </Box>
      </Box>
    );
  }

  const reason = live ? live.reason : REASON_KILL_SWITCH;
  const enabled = live ? live.enabled : false;
  const moduleDisabled = reason === REASON_MODULE_DISABLED;
  const capabilityMissing = !capability;
  const toggleDisabled = !capability || moduleDisabled || isSaving;

  let toggleTitle;
  let toggleDescription;
  let toggleTooltip = '';

  if (moduleDisabled) {
    toggleTitle = localizeMessage('search.settings.live.status.module_disabled');
    toggleDescription = localizeMessage(
      'search.settings.live.tooltip.module_disabled',
    );
    toggleTooltip = localizeMessage(
      'search.settings.live.tooltip.module_disabled',
    );
  } else if (capabilityMissing) {
    toggleTitle = localizeMessage('search.settings.live.toggle.title');
    toggleDescription = localizeMessage(
      'search.settings.live.tooltip.capability_missing',
    );
    toggleTooltip = localizeMessage(
      'search.settings.live.tooltip.capability_missing',
    );
  } else {
    toggleTitle = localizeMessage('search.settings.live.toggle.title');
    toggleDescription = localizeMessage(
      'search.settings.live.toggle.description',
    );
  }

  // Two switch variants: compact (used by OperatorHome embed) and the
  // reusable row. The compact variant renders only the Switch + saving
  // spinner + error label, with no title/description — it fits in a
  // StatusCard action slot.
  //
  // The non-compact variant renders a single horizontal row:
  //   [title + description] ........ [switch]
  // with no Grid offsets and no `setting-item-container` indent. The
  // parent page supplies the surrounding section header when needed
  // (set `bare` to suppress the in-component title/description).
  const switchEl = compact ? (
    <Box
      data-testid="live-settings-toggle"
      data-compact="true"
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
    >
      <FormGroup>
        <Switch
          color="primary"
          size="small"
          checked={enabled}
          onChange={(e) => handleToggle(e.target.checked)}
          disabled={toggleDisabled}
          inputProps={{
            'data-testid': 'live-settings-switch',
            'aria-label': localizeMessage(
              'search.settings.live.toggle.title',
            ),
          }}
        />
      </FormGroup>
    </Box>
  ) : (
    <Box
      data-testid="live-settings-toggle"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      width="100%"
      flexWrap="wrap"
    >
      {!bare && (
        <Box flex="1 1 auto" mr={2} minWidth={0}>
          <Typography variant="body1" data-testid="live-settings-toggle-title">
            {toggleTitle}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            data-testid="live-settings-toggle-description"
          >
            {toggleDescription}
          </Typography>
        </Box>
      )}
      <FormGroup>
        <Switch
          color="primary"
          checked={enabled}
          onChange={(e) => handleToggle(e.target.checked)}
          disabled={toggleDisabled}
          inputProps={{
            'data-testid': 'live-settings-switch',
            'aria-label': localizeMessage(
              'search.settings.live.toggle.title',
            ),
          }}
        />
      </FormGroup>
    </Box>
  );

  return (
    <Box
      data-testid="live-settings"
      p={compact ? 0 : bare ? 0 : 2}
      data-compact={compact ? 'true' : 'false'}
      data-bare={bare ? 'true' : 'false'}
      width="100%"
    >
      {showStatus && !compact && !bare && (
        <Box mb={2} data-testid="live-settings-status">
          <Typography variant="overline" color="textSecondary">
            {localizeMessage('search.settings.live.status.label')}
          </Typography>
          <Typography variant="h6" data-testid="live-settings-status-value">
            {localizeMessage(statusKeyFor(reason))}
          </Typography>
        </Box>
      )}

      {toggleTooltip && !compact ? (
        <Tooltip
          title={toggleTooltip}
          placement="right"
          arrow
          data-testid="live-settings-toggle-tooltip"
        >
          <span data-testid="live-settings-toggle-wrapper">{switchEl}</span>
        </Tooltip>
      ) : (
        <span data-testid="live-settings-toggle-wrapper">{switchEl}</span>
      )}

      {isSaving && (
        <Box
          mt={1}
          data-testid="live-settings-saving"
          display="flex"
          alignItems="center"
        >
          <CircularProgress size={16} />
          <Box ml={1}>
            <Typography variant="caption">
              {localizeMessage('search.settings.live.saving')}
            </Typography>
          </Box>
        </Box>
      )}

      {showStatus && !compact && !hideRefresh && !isSaving && (
        <Box mt={2}>
          <Button
            size="small"
            onClick={load}
            data-testid="live-settings-refresh"
          >
            {localizeMessage('search.settings.live.refresh')}
          </Button>
        </Box>
      )}

      {errorMessage && !compact && (
        <Box mt={2} data-testid="live-settings-error">
          <Typography color="error" variant="body2">
            {localizeMessage('search.settings.live.error', {
              error: errorMessage,
            })}
          </Typography>
          <Button
            size="small"
            onClick={load}
            data-testid="live-settings-retry"
          >
            {localizeMessage('search.settings.live.retry')}
          </Button>
        </Box>
      )}

      {errorMessage && compact && (
        <Box
          mt={1}
          data-testid="live-settings-error"
          data-compact="true"
        >
          <Typography
            color="error"
            variant="caption"
            data-testid="live-settings-error-text"
          >
            {localizeMessage('search.settings.live.error_compact', {
              error: errorMessage,
            })}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

LiveKillSwitchControl.propTypes = {
  onRegisterRef: PropTypes.func,
  // When provided, invoked with the new live state after every transition.
  // Payload: { enabled, reason, source: 'load' | 'optimistic' | 'success' | 'rollback' | 'load_error' }.
  // Use this to keep an external status display (e.g. OperatorHome StatusCard)
  // in sync with the toggle's local state.
  onLiveStateChange: PropTypes.func,
  // When true (default), render the standalone "Status: …" block and the
  // refresh button. When false, render only the switch + saving + error UI;
  // the parent (e.g. OperatorHome live StatusCard) is expected to provide
  // the merchant-facing status text.
  showStatus: PropTypes.bool,
  // When true, render a compact switch-only variant suitable for embedding
  // in a StatusCard action slot. Disables the Grid layout, the tooltip,
  // the status block, and the full error block; the saving spinner and a
  // one-line error caption are still shown.
  compact: PropTypes.bool,
  // When true, do not render the "Refresh" button. The parent page
  // (which is responsible for committing the toggle in deferWrite mode)
  // already controls re-loading via its own flow.
  hideRefresh: PropTypes.bool,
  // When true, clicking the toggle does NOT call setLiveEnabled() and
  // does NOT dispatch a toast. The control only updates its local
  // optimistic state and emits
  //   onLiveStateChange({ ..., source: 'pending' })
  // so the parent page can collect the desired value and commit it via
  // its own Save button.
  deferWrite: PropTypes.bool,
  // When true, the control does NOT render its own title/description
  // copy or its own status block. The parent page is expected to
  // provide the surrounding section header (title + description) and
  // the row is laid out as a single full-width flex line. Useful when
  // the control is embedded as a row inside a parent-managed section
  // card so the section header does not collide with the in-component
  // status block.
  bare: PropTypes.bool,
};

LiveKillSwitchControl.defaultProps = {
  onRegisterRef: null,
  onLiveStateChange: null,
  showStatus: true,
  compact: false,
  hideRefresh: false,
  deferWrite: false,
  bare: false,
};

export default LiveKillSwitchControl;
